import React from 'react';
import { ArrowLeftIcon, LockIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { ACADEMY_LINES } from '../../../data/academyCourses';
import { SECTORS } from '../../../data/services';
import { useAcademyHub } from '../../../context/AcademyHub';
import { Button } from '../../ui/Button';
import { Field, inputClass } from '../../ui/Field';
import { useCourseBuilder, wordCount } from './useCourseBuilder';
import type { AcademyLine, Course, Difficulty } from '../../../types/academy';
interface CourseBuilderProps {
  /** Absent when building a new course. */
  course?: Course;
  onDone: (message: string) => void;
  onCancel: () => void;
}
const DIFFICULTIES: Difficulty[] = ['Beginner', 'Intermediate', 'Advanced'];
export function CourseBuilder({
  course,
  onDone,
  onCancel
}: CourseBuilderProps) {
  const {
    isPublished,
    saveCourse,
    createCourse
  } = useAcademyHub();
  const live = Boolean(course && isPublished(course.slug));
  const b = useCourseBuilder(course, live);
  const errorRef = React.useRef<HTMLDivElement>(null);
  const words = wordCount(b.details.description);
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (b.validate().length) {
      window.requestAnimationFrame(() => errorRef.current?.focus());
      return;
    }
    const draft = b.build();
    if (!course) {
      createCourse(draft);
      onDone(`“${draft.title}” saved as a draft. Publish it from the course list when it is ready.`);
      return;
    }
    const sent = saveCourse({
      ...draft,
      slug: course.slug,
      number: course.number
    }, {
      notify: live && b.notify,
      note: b.note
    });
    onDone(live && b.notify ? `Changes saved. ${sent} enrolled ${sent === 1 ? 'learner was' : 'learners were'} notified.` : 'Changes saved.');
  };
  return <form onSubmit={submit} noValidate data-id="element-340" data-mp="3jyguiltmar1m">
      <button type="button" onClick={onCancel} className="inline-flex items-center gap-2 text-small text-royal hover:text-ink" data-id="element-341" data-mp="3jxf5fdfro40w">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-342" data-mp="3f05jw8vi0cic" />
        All courses
      </button>
      <h2 className="mt-4 font-display text-[26px] leading-tight text-ink" data-id="element-343" data-mp="35aggpq8annpv">{course ? `Edit: ${course.title}` : 'New course'}</h2>
      <p className="mt-1 text-[14px] text-body" data-id="element-344" data-mp="2vdlkxf3eo1fa">
        {course ? live ? 'This course is live. You can rename and rewrite anything and add modules or lessons; removing them would break learners’ saved progress.' : 'This course is a draft, so you can change its structure freely.' : 'New courses are saved as drafts. Learners see them once a platform admin publishes.'}
      </p>

      {b.errors.length > 0 && <div ref={errorRef} tabIndex={-1} role="alert" className="mt-6 rounded-md border border-[#FF8B7A]/50 px-5 py-4 text-[14px] text-ink focus:outline-none" data-id="element-345" data-mp="3ebwsa1auhvvo">
          <p className="font-medium" data-id="element-346" data-mp="av7j07jvo82b">Fix these before saving:</p>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-body" data-id="element-347" data-mp="atwgeb2656cs">
            {b.errors.map(e => <li key={e} data-id="element-348" data-mp="foaj6yj5ufrh">{e}</li>)}
          </ul>
        </div>}

      <section aria-labelledby="details" className="mt-10 grid gap-10 lg:grid-cols-[220px_1fr]" data-id="element-349" data-mp="20as0646tvi3g">
        <h3 id="details" className="text-[16px] font-medium text-ink" data-id="element-350" data-mp="1vbnsjhzjxsf8">
          Course details
        </h3>
        <div className="space-y-5" data-id="element-351" data-mp="2a4h813etq8jw">
          <Field id="cb-title" label={`Title (${b.details.title.length}/80)`} required data-id="element-352" data-mp="2531pbi04ce4o">
            <input id="cb-title" value={b.details.title} maxLength={80} onChange={e => b.setDetail('title', e.target.value)} className={inputClass} data-id="element-353" data-mp="1hdqwqnsx9q1m" />
          </Field>
          <div className="grid gap-5 sm:grid-cols-3" data-id="element-354" data-mp="1ch0blm29g49c">
            <Field id="cb-line" label="Service line" data-id="element-355" data-mp="1qfqa964qabzt">
              <select id="cb-line" value={b.details.serviceLine} onChange={e => b.setDetail('serviceLine', e.target.value as AcademyLine)} className={inputClass} data-id="element-356" data-mp="1md3fawcrgaye">
                {ACADEMY_LINES.map(l => <option key={l.id} value={l.id} data-id="element-357" data-mp="2xsimesyooj78">
                    Cavalla {l.name}
                  </option>)}
              </select>
            </Field>
            <Field id="cb-level" label="Level" data-id="element-358" data-mp="3t4i1fll7rl4g">
              <select id="cb-level" value={b.details.difficulty} onChange={e => b.setDetail('difficulty', e.target.value as Difficulty)} className={inputClass} data-id="element-359" data-mp="xuueho925uw">
                {DIFFICULTIES.map(d => <option key={d} data-id="element-360" data-mp="axu3qgshu08t">{d}</option>)}
              </select>
            </Field>
            <Field id="cb-hours" label="Estimated hours" data-id="element-361" data-mp="fv406geavc65">
              <input id="cb-hours" type="number" min="0.5" step="0.5" value={b.details.hours} onChange={e => b.setDetail('hours', e.target.value)} className={inputClass} data-id="element-362" data-mp="jx8rh8h9zwpm" />
            </Field>
          </div>
          <fieldset data-id="element-363" data-mp="tntlsjpx2f2q">
            <legend className="text-[14px] font-medium text-ink" data-id="element-364" data-mp="tm8r8u3lqw4v">Sectors</legend>
            <label className="mt-3 flex items-center gap-2 text-[14px] text-body" data-id="element-365" data-mp="ymwovymc6b82">
              <input type="checkbox" checked={b.details.allSectors} onChange={e => b.setDetail('allSectors', e.target.checked)} className="accent-[#FF5C1A]" data-id="element-366" data-mp="13l81gh40mztz" />
              All sectors
            </label>
            {!b.details.allSectors && <div className="mt-3 flex flex-wrap gap-2" data-id="element-367" data-mp="2j2ujgv7kisnq">
                {SECTORS.map(s => {
              const on = b.details.sectors.includes(s);
              return <button key={s} type="button" aria-pressed={on} onClick={() => b.setDetail('sectors', on ? b.details.sectors.filter(x => x !== s) : [...b.details.sectors, s])} className={`rounded-full border px-3.5 py-1.5 text-[13px] transition-colors duration-150 ${on ? 'border-gold bg-charcoal text-ink' : 'border-line text-body hover:border-gold/50'}`} data-id="element-368" data-mp="297jev5pvj1tp">
                      {s}
                    </button>;
            })}
              </div>}
          </fieldset>
          <Field id="cb-desc" label="Description" hint={`${words} words. Shown on the catalogue and course page; aim for 150 to 300.`} required data-id="element-369" data-mp="1ivz6vutqxrcc">
            <textarea id="cb-desc" rows={6} value={b.details.description} onChange={e => b.setDetail('description', e.target.value)} className={inputClass} data-id="element-370" data-mp="1nxeqrjk6trt5" />
          </Field>
        </div>
      </section>

      <section aria-labelledby="curriculum" className="mt-12 grid gap-10 border-t border-line pt-10 lg:grid-cols-[220px_1fr]" data-id="element-371" data-mp="1e36256ya2j09">
        <div data-id="element-372" data-mp="147ulgj3b71pv">
          <h3 id="curriculum" className="text-[16px] font-medium text-ink" data-id="element-373" data-mp="147l41jwby2vs">
            Curriculum
          </h3>
          <p className="mt-1 text-small text-muted" data-id="element-374" data-mp="q8v8w9t2mda2">Lesson notes appear under the video on each lesson page.</p>
        </div>
        <div data-id="element-375" data-mp="v6wr6sioa8kh">
          <ol className="space-y-6" data-id="element-376" data-mp="v8hm4j8wftiv">
            {b.modules.map((m, mi) => <li key={m.key} className="rounded-md border border-line p-5" data-id="element-377" data-mp="2kme071wiczmx" data-mpi={mi}>
                <div className="flex items-center justify-between gap-3" data-id="element-378" data-mp="2pjcvhnrwrcmp">
                  <p className="text-small font-medium text-muted" data-id="element-379" data-mp="dwsr9ipw0o46">Module {mi + 1}</p>
                  {m.locked ? <span className="inline-flex items-center gap-1 text-[12px] text-muted" data-id="element-380" data-mp="8zkanmke0s51">
                      <LockIcon className="h-3 w-3" aria-hidden="true" data-id="element-381" data-mp="no6yydrzsu8e" />
                      Live, cannot be removed
                    </span> : b.modules.length > 1 && <button type="button" onClick={() => b.removeModule(m.key)} className="inline-flex items-center gap-1 text-small text-muted hover:text-ink" data-id="element-382" data-mp="wm8vag0s6ypc">
                        <Trash2Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-383" data-mp="16mj83ziwm43z" />
                        Remove module
                      </button>}
                </div>
                <div className="mt-3 grid gap-4 sm:grid-cols-2" data-id="element-384" data-mp="38cl1u3p3k1yw">
                  <Field id={`m-${m.key}-title`} label="Module title" required data-id="element-385" data-mp="33e9qtpn2flf3">
                    <input id={`m-${m.key}-title`} value={m.title} onChange={e => b.updateModule(m.key, {
                  title: e.target.value
                })} className={inputClass} data-id="element-386" data-mp="33gcrr4prayyl" />
                  </Field>
                  <Field id={`m-${m.key}-quiz`} label="Quiz focus" required data-id="element-387" data-mp="2micm9926zldq">
                    <input id={`m-${m.key}-quiz`} value={m.quizFocus} onChange={e => b.updateModule(m.key, {
                  quizFocus: e.target.value
                })} className={inputClass} data-id="element-388" data-mp="2rc8fjy7igk2k" />
                  </Field>
                </div>
                <p className="mt-2 text-small text-muted" data-id="element-389" data-mp="1ynt4fmri2lwi">
                  {m.quiz?.length ? `${m.quiz.length} quiz questions written, kept as they are.` : 'Quiz not written yet; learners finish the module by completing its lessons.'}
                </p>

                <ol className="mt-5 space-y-4 border-l border-line pl-4" data-id="element-390" data-mp="2cmj0cz2l2fja">
                  {m.lessons.map((l, li) => <li key={l.key} data-id="element-391" data-mp="3jyjhpdarmt1h" data-mpi={li}>
                      <div className="flex items-end gap-2" data-id="element-392" data-mp="3k1nq6mv9kaxy">
                        <div className="flex-1" data-id="element-393" data-mp="3o04w532y7fds">
                          <Field id={`l-${l.key}`} label={`Lesson ${li + 1}`} required data-id="element-394" data-mp="1cdtdrff136il">
                            <input id={`l-${l.key}`} value={l.title} onChange={e => b.updateLesson(m.key, l.key, {
                        title: e.target.value
                      })} className={inputClass} data-id="element-395" data-mp="1cfnnryorhe7w" />
                          </Field>
                        </div>
                        {!l.locked && m.lessons.length > 1 && <button type="button" onClick={() => b.removeLesson(m.key, l.key)} aria-label={`Remove lesson ${li + 1}`} className="mb-1 rounded-sm p-2 text-muted hover:bg-panel hover:text-ink" data-id="element-396" data-mp="ykbtttifjrs1">
                            <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-397" data-mp="foc0ofdpui6v" />
                          </button>}
                      </div>
                      <label htmlFor={`n-${l.key}`} className="sr-only" data-id="element-398" data-mp="2a1lbqblwubr8">
                        Notes for lesson {li + 1}
                      </label>
                      <textarea id={`n-${l.key}`} rows={2} value={l.notes} onChange={e => b.updateLesson(m.key, l.key, {
                  notes: e.target.value
                })} placeholder="Lesson notes: key takeaways, definitions, worked example (optional)" className={`${inputClass} mt-2 text-[14px]`} data-id="element-399" data-mp="3dmurvbv1ueps" />
                    </li>)}
                </ol>
                <button type="button" onClick={() => b.addLesson(m.key)} className="mt-4 inline-flex items-center gap-1.5 text-small text-royal hover:text-ink" data-id="element-400" data-mp="2q06acr9yco9p">
                  <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-401" data-mp="2yyrq6eo1hp6s" />
                  Add lesson
                </button>
              </li>)}
          </ol>
          <Button type="button" variant="secondary" onClick={b.addModule} className="mt-6" data-id="element-402" data-mp="2b7chm45o6bn3">
            <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-403" data-mp="268rr4wg7z6bu" />
            Add module
          </Button>
        </div>
      </section>

      {live && <section aria-labelledby="announce" className="mt-12 grid gap-10 border-t border-line pt-10 lg:grid-cols-[220px_1fr]" data-id="element-404" data-mp="qkbo5xb4fd4h">
          <h3 id="announce" className="text-[16px] font-medium text-ink" data-id="element-405" data-mp="ctq4w7hr9162">
            Tell learners
          </h3>
          <div className="space-y-4" data-id="element-406" data-mp="hovkypoh7lm8">
            <label className="flex items-start gap-3 text-[15px] text-ink" data-id="element-407" data-mp="15g10t4uf2dwj">
              <input type="checkbox" checked={b.notify} onChange={e => b.setNotify(e.target.checked)} className="mt-1 accent-[#FF5C1A]" data-id="element-408" data-mp="19i5s3wxe6yg0" />
              <span data-id="element-409" data-mp="vhbmtn5n5dlk">
                Notify enrolled learners
                <span className="block text-small text-muted" data-id="element-410" data-mp="10h7npdj1koww">They get a notification and see an update banner on the course page. Their progress is kept.</span>
              </span>
            </label>
            {b.notify && <Field id="cb-note" label="What changed" hint="One or two sentences, shown in the notification." data-id="element-411" data-mp="3l8q9z826myyn">
                <textarea id="cb-note" rows={2} value={b.note} onChange={e => b.setNote(e.target.value)} placeholder="Added a lesson on the EU AI Act timeline and refreshed the Module 2 notes." className={inputClass} data-id="element-412" data-mp="314ap2rrhs9mq" />
              </Field>}
          </div>
        </section>}

      <div className="mt-12 flex flex-col-reverse gap-3 border-t border-line pt-6 sm:flex-row sm:justify-end" data-id="element-413" data-mp="2ige2vphwki5g">
        <Button type="button" variant="secondary" onClick={onCancel} data-id="element-414" data-mp="2di43f2p5ave1">
          Cancel
        </Button>
        <Button type="submit" data-id="element-415" data-mp="29g94u5ylmw3y">{course ? live && b.notify ? 'Save and notify learners' : 'Save changes' : 'Save as draft'}</Button>
      </div>
    </form>;
}